import { z } from 'zod';
import { isoDaySchema } from './dates';

// ---------------------------------------------------------------------------------------------
// Auditoria e histórico (T-202, RF-46, RN-16, PRD §18; DEC-067)

export const AUDIT_SOURCES = ['SCREEN', 'IMPORT', 'CALCULATION', 'JOB'] as const;
export type AuditSourceName = (typeof AUDIT_SOURCES)[number];
export const AUDIT_SOURCE_LABELS: Record<AuditSourceName, string> = {
  SCREEN: 'Tela',
  IMPORT: 'Importação',
  CALCULATION: 'Cálculo',
  JOB: 'Processo automático',
};

export const AUDIT_ACTION_LABELS: Record<'CREATE' | 'UPDATE' | 'DELETE' | 'ARCHIVE', string> = {
  CREATE: 'Criação',
  UPDATE: 'Alteração',
  DELETE: 'Exclusão',
  ARCHIVE: 'Arquivamento',
};

/** Nome das entidades da trilha, como a pessoa lê. */
export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  organization: 'Empresa',
  bsc_project: 'Projeto BSC',
  user: 'Usuário',
  role: 'Perfil',
  department: 'Área',
  business_unit: 'Unidade',
  consultant_assignment: 'Consultor da empresa',
  perspective: 'Perspectiva',
  guideline: 'Diretriz',
  premise: 'Premissa',
  objective: 'Objetivo',
  kpi: 'Indicador (KPI)',
  kpi_custom_unit: 'Unidade de medida',
  kpi_target: 'Meta',
  kpi_result: 'Resultado',
  target_settings: 'Regras das metas',
  performance_settings: 'Regras de performance',
  period_settings: 'Regras do fechamento',
  period_closing: 'Fechamento de período',
  alert: 'Alerta',
  alert_settings: 'Regras dos alertas',
  initiative: 'Iniciativa',
  action_plan: 'Plano de ação',
  action: 'Ação',
  action_checklist_item: 'Item do checklist',
  action_update: 'Atualização da ação',
  meeting: 'Reunião',
  meeting_agenda: 'Pauta',
  meeting_agenda_item: 'Item da pauta',
  meeting_decision: 'Decisão',
  meeting_decision_approver: 'Aprovação da decisão',
  collab_item: 'Comentário ou anexo',
  diagnosis_item: 'Item do diagnóstico',
  scenario: 'Cenário',
  budget_item: 'Item do orçamento',
  import_template: 'Modelo de importação',
  project_template: 'Modelo de projeto',
  notification_preference: 'Preferências de notificação',
};

/** Nome dos campos mais comuns (os outros aparecem como gravados). */
export const AUDIT_FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  legalName: 'Razão social',
  title: 'Título',
  text: 'Texto',
  description: 'Descrição',
  notes: 'Observações',
  code: 'Código',
  symbol: 'Símbolo',
  value: 'Valor',
  referenceDate: 'Data de referência',
  justification: 'Justificativa',
  status: 'Situação',
  archivedAt: 'Situação',
  archiveReason: 'Motivo do arquivamento',
  archivedByName: 'Arquivado por',
  weight: 'Peso',
  position: 'Ordem',
  ownerId: 'Responsável',
  leaderId: 'Líder',
  responsibleId: 'Responsável',
  consultantId: 'Consultor',
  surveyedById: 'Levantado por',
  surveyedByName: 'Levantado por',
  departmentId: 'Área',
  departmentIds: 'Áreas',
  businessUnitId: 'Unidade',
  businessUnitIds: 'Unidades',
  perspectiveId: 'Perspectiva',
  objectiveId: 'Objetivo',
  objectives: 'Objetivos',
  kpiId: 'Indicador',
  initiativeId: 'Iniciativa',
  alertId: 'Alerta',
  decisionId: 'Decisão',
  diagnosisItemId: 'Item do diagnóstico',
  parentId: 'Superior',
  relations: 'Relações',
  startDate: 'Início',
  endDate: 'Fim',
  startsAt: 'Início',
  durationMinutes: 'Duração (minutos)',
  dueDate: 'Prazo',
  completedAt: 'Concluída em',
  email: 'E-mail',
  roleId: 'Perfil',
  permissions: 'Permissões',
  approvalStatus: 'Aprovação',
  approvalRequired: 'Aprovação de metas obrigatória',
  category: 'Categoria',
  type: 'Tipo',
  kind: 'Tipo',
  level: 'Nível',
  horizon: 'Horizonte',
  criticality: 'Criticidade',
  impact: 'Impacto',
  urgency: 'Urgência',
  effort: 'Esforço',
  sentToMatrixAt: 'Enviado à matriz em',
  originType: 'Origem',
  months: 'Valores mensais',
  premises: 'Premissas',
  targets: 'Metas',
  isBase: 'Cenário base',
  baseSetAt: 'Escolhido como base em',
  progress: 'Progresso (%)',
  progressMode: 'Forma do progresso',
  budgetPlanned: 'Orçamento previsto',
  budgetActual: 'Orçamento realizado',
  expectedImpact: 'Impacto esperado',
  team: 'Equipe',
  risks: 'Riscos',
  why: 'Por quê',
  how: 'Como',
  location: 'Onde',
  cost: 'Quanto custa',
  requiresEvidence: 'Exige evidência',
  evidence: 'Evidência',
  isEvidence: 'É evidência',
  done: 'Feito',
  doneByName: 'Feito por',
  participants: 'Participantes',
  horizonStart: 'Início do horizonte',
  horizonEnd: 'Fim do horizonte',
  phase: 'Fase',
  logoKey: 'Logotipo',
  columns: 'Colunas do modelo',
  companyRule: 'Regra da performance da empresa',
  performanceCap: 'Teto da performance',
  requireRedJustification: 'Justificativa obrigatória no vermelho',
  consecutiveDropPeriods: 'Quedas seguidas para alertar',
  objectiveRedKpis: 'KPIs vermelhos para alertar o objetivo',
  staleGraceDays: 'Dias de tolerância para lançamento',
  greenMin: 'Verde a partir de',
  yellowMin: 'Amarelo a partir de',
  periodStart: 'Início do período',
  periodEnd: 'Fim do período',
};

const cursorRegex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z\|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const auditQuerySchema = z
  .object({
    userId: z.string().uuid().optional(),
    entity: z
      .string()
      .regex(/^[a-z_]+$/, 'Entidade inválida.')
      .optional(),
    entityId: z.string().min(1).max(64).optional(),
    /** Histórico de um KPI: o cadastro dele e as metas, resultados e alertas dele. */
    kpiId: z.string().uuid().optional(),
    from: isoDaySchema.optional(),
    to: isoDaySchema.optional(),
    source: z.enum(AUDIT_SOURCES).optional(),
    /** Cursor "instante|id da mudança" da última mudança da página anterior. */
    before: z
      .string()
      .regex(cursorRegex, 'Cursor inválido.')
      .refine((v) => {
        const at = new Date(v.split('|')[0]!);
        return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 19) === v.slice(0, 19);
      }, 'Cursor inválido.')
      .optional(),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: 'O fim vem depois do início.',
    path: ['to'],
  });

export const auditEntrySchema = z.object({
  changeId: z.string().uuid(),
  occurredAt: z.string(),
  actorUserId: z.string().uuid().nullable(),
  actorName: z.string().nullable(),
  source: z.enum(AUDIT_SOURCES),
  action: z.enum(['CREATE', 'UPDATE', 'DELETE', 'ARCHIVE']),
  entity: z.string(),
  entityLabel: z.string(),
  entityId: z.string(),
  /** Nome do que foi alterado (ex.: "REC — Receita"), quando ainda existe. */
  entityName: z.string().nullable(),
  reason: z.string().nullable(),
  /** Onde a meta ou o resultado vale e o período ("Mar/2026 · Consolidado"); null nas outras entidades. */
  detail: z.string().nullable(),
  fields: z.array(z.object({ field: z.string(), label: z.string(), before: z.string(), after: z.string() })),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditPageSchema = z.object({
  items: z.array(auditEntrySchema),
  nextCursor: z.string().nullable(),
});

export const auditOptionsSchema = z.object({
  users: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  entities: z.array(z.object({ key: z.string(), label: z.string() })),
});

export const AUDIT_PAGE = 25;

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Valor da trilha como a pessoa lê (PRD §18): número no padrão brasileiro, data e hora, sim/não, listas e "—"
 * para vazio. Nomes de pessoas, áreas e afins chegam já resolvidos pela API (`names`).
 */
export function auditValueText(v: unknown, names: ReadonlyMap<string, string> = new Map()): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (typeof v === 'number') return v.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
  if (typeof v === 'string') {
    if (names.has(v)) return names.get(v)!;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) {
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    return v.length > 500 ? `${v.slice(0, 500)}…` : v;
  }
  if (Array.isArray(v)) return v.length ? v.map((x) => auditValueText(x, names)).join('; ') : '—';
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>);
    return entries.length ? entries.map(([k, x]) => `${k}: ${auditValueText(x, names)}`).join(', ') : '—';
  }
  return String(v);
}
